<template lang="pug">
TheBibleToolbar
BibleGrid.fill-height(
  v-model:displayed-range="displayedRange"
  :tids="openTids"
  :local-tid="localTid"
)
VBtn.text-black(
  :to="localePath(getBibleSearchLocation())"
  :icon="true"
  color="secondary"
  size="small"
  title="Bible search"
  aria-label="Bible search"
  position="fixed"
  style="bottom: 6em; right: 1em"
)
  VIcon {{ mdiMagnify }}
</template>

<script setup lang="ts">
import { computed } from "vue";
import { mdiMagnify } from "@mdi/js";
import { allTranslations } from "~/static/translations";
import { useBibleStore } from "~/stores/useBibleStore";
import { injectRequired } from "~/injectRequired";
import { useComputedResultAsync } from "~/composables/useComputedResultAsync";
import { getBibleSearchLocation } from "~/routing/locations";
import { ScriptureClientKey } from "~/injectionKeys";
import { localesByCode } from "~/i18n/locales";
import { never } from "~/never";
import TheBibleToolbar from "~/components/TheBibleToolbar.vue";

definePageMeta({
  key: false, // This prevents recreating the `NuxtPage` component when we do `router.push`. We want to reuse the page.
  middleware: ["bible"],
});

const scriptureClient = injectRequired(ScriptureClientKey);

const localePath = useLocalePath();
const i18n = useI18n();
const localeConfig = computed(
  () => localesByCode.get(i18n.locale.value) ?? never("Locale not found."),
);
const localTid = computed<string>(() => localeConfig.value.defaultTid);

const bibleStore = useBibleStore();
const runtimeConfig = useRuntimeConfig();
const displayedRange = computed({
  get: () =>
    bibleStore.getDisplayedRange(runtimeConfig.public.bible.defaultVids),
  set: (value) => bibleStore.setDisplayedRange(value),
});

const openTids = computed(() =>
  bibleStore.getOpenTranslations(localeConfig.value.defaultTid),
);

/**
 * A human-readable version of the displayed VID range.
 */
const humanReadableRange = useComputedResultAsync(() =>
  displayedRange.value.renderReference(
    scriptureClient,
    localTid.value,
    false,
    false,
  ),
);

const openTranslationsAbbreviations = computed<string>(() =>
  bibleStore
    .getOpenTranslations(localeConfig.value.defaultTid)
    .map((tid) => allTranslations[tid]?.abbreviation)
    .join(", "),
);

/**
 * The document title.
 */
const title = computed(
  () =>
    `${humanReadableRange.value ?? "Bible"} ${openTranslationsAbbreviations.value}`,
);

useHead({
  title,

  meta: [
    {
      name: "og:title",
      content: title,
    },
    {
      name: "og:description",
      content: () =>
        `Read ${humanReadableRange.value ?? "the Bible"} in ${openTranslationsAbbreviations.value}.`,
    },
  ],
});
</script>
